import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import appStore from '@stores/appStore'
import assistantStore, {
  AssistantConversationSummary,
} from '@stores/assistantStore'
import { message } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import { conversationTime, groupConversations } from './conversationGroups'
import { conversationPlacement } from './placements/registry'
import { KW } from './ui'

/** The longest title a conversation can be given (AssistantConsts.MaxConversationTitleLength). */
const MAX_TITLE_LENGTH = 60

interface Props {
  items: readonly AssistantConversationSummary[]
  /**
   * Names a conversation started from a placement with a badge ("Case history"). Off in a
   * placement's own History, where every row is one.
   */
  showBadges?: boolean
}

/**
 * The rows of a conversation history, grouped by day: open one, rename it in the row (Enter
 * saves, Escape puts the title back) or delete it, with the confirmation inside the row itself
 * (no pop-over), so nothing overflows the panel and the list never jumps. Renaming and deleting
 * go through the store, so every list that shows the conversation (the drawer's, a placement's
 * own) follows.
 */
const ConversationRows = ({ items, showBadges = true }: Props) => {
  const session = useAssistantSession()
  const { activeConversationId, loadingConversationId } = session
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameText, setRenameText] = useState('')
  const [savingRename, setSavingRename] = useState(false)
  const renameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    cancelRef.current?.focus()
  }, [confirmingId])

  useEffect(() => {
    renameRef.current?.focus()
    renameRef.current?.select()
  }, [renamingId])

  const deleteConversation = async (id: string) => {
    setDeletingId(id)
    try {
      await assistantStore.deleteConversation(id)
      setConfirmingId(null)
    } catch {
      message.error('The conversation could not be deleted. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const startRename = (id: string, title: string) => {
    setConfirmingId(null)
    setRenameText(title)
    setRenamingId(id)
  }

  const saveRename = async (id: string, currentTitle: string) => {
    const title = renameText.replace(/\s+/g, ' ').trim()
    if (!title || savingRename) {
      return
    }
    if (title === currentTitle) {
      setRenamingId(null)
      return
    }
    setSavingRename(true)
    try {
      await assistantStore.renameConversation(id, title)
      setRenamingId(null)
    } catch {
      // The row stays open with what was typed: nothing is lost, and the old title is still
      // the conversation's.
      message.error('The conversation could not be renamed. Please try again.')
    } finally {
      setSavingRename(false)
    }
  }

  return (
    <>
      {groupConversations(items.slice()).map(group => (
        <section key={group.label} aria-label={group.label}>
          <div className="kw-history-group">{group.label}</div>
          {group.items.map(item => {
            const title = item.title || 'Untitled conversation'
            const active = item.id === activeConversationId
            const opening = item.id === loadingConversationId
            // Still being answered in another view (a placement's pane, or the drawer): it
            // opens here once that answer is over, so no two views show it with different
            // content.
            const answeringElsewhere =
              !active &&
              assistantStore.isConversationBusyElsewhere(item.id, session)
            // Started from a placement (e.g. the Case history pane): named with a badge, and
            // it opens here like any other conversation.
            const origin = showBadges
              ? conversationPlacement(item.placement)
              : null
            const badgeId = origin
              ? `kw-history-placement-${item.id}`
              : undefined

            if (item.id === confirmingId) {
              return (
                <div
                  key={item.id}
                  className="kw-history-row kw-history-confirm"
                  role="alertdialog"
                  aria-label="Delete this conversation?"
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      // Used here: a pane neither leaves its History nor closes its modal.
                      e.preventDefault()
                      setConfirmingId(null)
                    }
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[13px] font-bold"
                      style={{ color: KW.red }}
                    >
                      Delete this conversation?
                    </div>
                    <div
                      className="kw-history-title"
                      style={{ color: KW.muted }}
                    >
                      {title}
                    </div>
                  </div>
                  <button
                    ref={cancelRef}
                    type="button"
                    className="kw-history-btn"
                    onClick={() => setConfirmingId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="kw-history-btn kw-history-btn-danger"
                    disabled={deletingId === item.id}
                    onClick={() => deleteConversation(item.id)}
                  >
                    {deletingId === item.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )
            }

            if (item.id === renamingId) {
              return (
                <form
                  key={item.id}
                  className="kw-history-row kw-history-renaming"
                  aria-label="Rename this conversation"
                  onSubmit={e => {
                    e.preventDefault()
                    saveRename(item.id, item.title ?? '')
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      e.stopPropagation()
                      setRenamingId(null)
                    }
                  }}
                >
                  <input
                    ref={renameRef}
                    type="text"
                    className="kw-history-filter"
                    value={renameText}
                    maxLength={MAX_TITLE_LENGTH}
                    aria-label="Conversation title"
                    disabled={savingRename}
                    onChange={e => setRenameText(e.target.value)}
                  />
                  <button
                    type="button"
                    className="kw-history-btn"
                    disabled={savingRename}
                    onClick={() => setRenamingId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="kw-history-btn kw-history-btn-primary"
                    disabled={savingRename || !renameText.trim()}
                  >
                    {savingRename ? 'Saving…' : 'Save'}
                  </button>
                </form>
              )
            }

            return (
              <div
                key={item.id}
                className={`kw-history-row${active ? ' is-active' : ''}`}
              >
                <button
                  type="button"
                  className="kw-history-open"
                  aria-current={active ? 'true' : undefined}
                  aria-busy={opening || answeringElsewhere || undefined}
                  // The badge is said as the row's description, after its title and time.
                  aria-describedby={badgeId}
                  // Not while an answer is coming in: it belongs to the open conversation.
                  disabled={session.isTurnRunning || answeringElsewhere}
                  onClick={() => session.selectConversation(item.id)}
                >
                  <span className="kw-history-title">{title}</span>
                  <span className="kw-history-meta">
                    <span
                      className="kw-history-time"
                      role={opening ? 'status' : undefined}
                    >
                      {opening
                        ? 'Opening…'
                        : answeringElsewhere
                        ? 'Answering…'
                        : conversationTime(
                            item.lastMessageAt,
                            appStore.currentUser?.dateFormat
                          )}
                    </span>
                    {origin && (
                      <span
                        id={badgeId}
                        className="kw-history-badge"
                        aria-hidden="true"
                      >
                        {origin.displayName}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  className="kw-history-delete kw-history-rename"
                  title="Rename conversation"
                  aria-label={`Rename conversation: ${title}`}
                  onClick={() => startRename(item.id, item.title ?? '')}
                >
                  <EditOutlined />
                </button>
                <button
                  type="button"
                  className="kw-history-delete"
                  title="Delete conversation"
                  aria-label={`Delete conversation: ${title}`}
                  onClick={() => setConfirmingId(item.id)}
                >
                  <DeleteOutlined />
                </button>
              </div>
            )
          })}
        </section>
      ))}
    </>
  )
}

export default observer(ConversationRows)
